// touchGestureAnalytics — fire-and-forget instrumentation for phone pinch
// reliability (modern-phone-shell-2026-07-02.md §7). Measurement only: the
// grace window (TOUCH_GRACE_WINDOW_MS) and the "late 2nd touch is swallowed"
// behaviour are unchanged by this module.
//
// Same Transport-port-only, best-effort send as `layoutSwitchAnalytics.ts`.

import { TOUCH_GESTURE_DELAY_MAX_MS } from '@oekaki/protocol';
import type { Transport } from './ports/Transport.js';

export type TouchGestureOutcome = 'started' | 'late';

/**
 * Report a touch-gesture outcome. Fire-and-forget: no-op when the socket is
 * not open, swallows any send error (must never disturb drawing / gestures).
 * `delayMs` is only meaningful for `late`; omit for `started`.
 */
export const reportTouchGesture = (
  transport: Transport,
  outcome: TouchGestureOutcome,
  delayMs?: number,
): void => {
  if (transport.status.t !== 'open') return;
  try {
    transport.send(
      delayMs === undefined
        ? { t: 'analytics.touch-gesture', outcome }
        : { t: 'analytics.touch-gesture', outcome, delayMs },
    );
  } catch {
    // best-effort
  }
};

/** Round to an integer ms and clamp into the protocol range 0..TOUCH_GESTURE_DELAY_MAX_MS. */
export const clampTouchGestureDelayMs = (ms: number): number => {
  if (!Number.isFinite(ms)) return 0;
  return Math.min(TOUCH_GESTURE_DELAY_MAX_MS, Math.max(0, Math.round(ms)));
};
